import React from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, MessageCircle, Clock, Building2 } from "lucide-react";
import AnimatedSection from "../components/common/AnimatedSection";
import ContactForm from "../components/forms/ContactForm";
import {
  OFFICE_ADDRESS,
  OFFICE_MAP_URL,
  OFFICE_MAP_EMBED_URL,
  FACTORY_ADDRESS,
  FACTORY_MAP_URL,
  LEGAL_NAME,
  REGISTERED_ADDRESS,
  GSTIN,
} from "../utils/locations";

const contactInfo = [
  {
    icon: MapPin,
    title: "Office",
    content: OFFICE_ADDRESS,
    mapLink: OFFICE_MAP_URL,
  },
  {
    icon: MapPin,
    title: "Factory",
    content: FACTORY_ADDRESS,
    mapLink: FACTORY_MAP_URL,
  },
  {
    icon: Building2,
    title: "Registered Office",
    content: `${LEGAL_NAME}, ${REGISTERED_ADDRESS}`,
    note: `GSTIN: ${GSTIN}`,
  },
  {
    icon: MessageCircle,
    title: "WhatsApp Us",
    content: "+91 799 565 9797",
    link: "https://wa.me/917995659797?text=Hi, I'm interested in Iravatham Interiors. Please share more details.",
  },
  {
    icon: Clock,
    title: "Working Hours",
    content: "Mon - Sat: 10am - 7pm",
  },
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const defaultType = searchParams.get("type") || "b2c";

  return (
    <div>
      {/* Hero */}
      <section className="py-32 lg:py-40 bg-gradient-brand">
        <div className="container-custom section-padding">
          <AnimatedSection className="max-w-3xl text-white">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              Get In Touch
            </span>
            <h1 className="font-heading text-display-1 mt-4 mb-6 text-white">
              Let's discuss your project.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              Whether you're planning your dream home or a large-scale project,
              our team is ready to help you create something exceptional.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Info */}
            <AnimatedSection>
              <div className="mb-10">
                <h2 className="font-heading text-h3 text-brand-primary mb-4">
                  We'd love to hear from you.
                </h2>
                <p className="text-text-secondary leading-relaxed">
                  Book a consultation with our design experts. We'll understand
                  your needs, show you possibilities, and provide a detailed
                  estimate – all with no obligation.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                {contactInfo.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 bg-brand-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-brand-accent" />
                    </div>
                    <div>
                      <h4 className="font-medium text-brand-primary mb-1">
                        {item.title}
                      </h4>
                      {item.link ? (
                        <a
                          href={item.link}
                          target={
                            item.link.startsWith("http") ? "_blank" : undefined
                          }
                          rel={
                            item.link.startsWith("http")
                              ? "noopener noreferrer"
                              : undefined
                          }
                          className="text-text-secondary hover:text-brand-accent transition-colors"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-text-secondary">{item.content}</p>
                      )}
                      {item.note && (
                        <p className="text-sm text-text-secondary mt-1">
                          {item.note}
                        </p>
                      )}
                      {item.mapLink && (
                        <a
                          href={item.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-1 text-sm font-medium text-brand-accent hover:text-brand-accent-hover transition-colors"
                        >
                          Open in Maps →
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="aspect-video rounded-2xl overflow-hidden bg-background-card">
                <iframe
                  title="Iravatham Location"
                  src={OFFICE_MAP_EMBED_URL}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </AnimatedSection>

            {/* Right - Form */}
            <div>
              <ContactForm defaultType={defaultType} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
